import { Link } from 'react-router-dom';
import {
    BarChart3,
    KeyRound,
    Trash2,
    User,
    UserCog,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MemberViewStatisticsIcon } from '../../assets/icons/member-view-statistics.svg';

function AccountActionRow({
    icon: Icon,
    title,
    description,
    children,
    variant = 'default',
}) {
    const isDanger = variant === 'danger';
    const iconWrapClass = isDanger
        ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'
        : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300';

    return (
        <div
            className={`flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 lg:px-5 lg:py-4 ${
                isDanger ? 'bg-red-50/50 dark:bg-red-950/10' : ''
            }`}
        >
            <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${iconWrapClass}`}>
                    <Icon size={18} aria-hidden />
                </span>
                <div className="min-w-0">
                    <h3 className={`text-sm font-semibold lg:text-base ${isDanger ? 'text-red-800 dark:text-red-200' : 'text-gray-900 dark:text-white'}`}>
                        {title}
                    </h3>
                    {description ? (
                        <p className="mt-0.5 text-sm leading-snug text-gray-500 dark:text-white/60">{description}</p>
                    ) : null}
                </div>
            </div>
            <div className="shrink-0 sm:pl-2">{children}</div>
        </div>
    );
}

function AccountPanel({ title, description, children, variant = 'default' }) {
    const borderClass = variant === 'danger'
        ? 'border-red-200 dark:border-red-900/40'
        : 'border-gray-200 dark:border-white/10';

    return (
        <section className={`overflow-hidden rounded-2xl border bg-white shadow-sm dark:bg-gray-800 ${borderClass}`}>
            <div className={`border-b px-4 py-3 lg:px-5 ${
                variant === 'danger'
                    ? 'border-red-100 bg-red-50/80 dark:border-red-900/30 dark:bg-red-950/20'
                    : 'border-gray-100 bg-gradient-to-r from-gray-50 to-white dark:border-white/5 dark:from-gray-900/80 dark:to-gray-800'
            }`}>
                <h3 className={`text-sm font-semibold lg:text-base ${variant === 'danger' ? 'text-red-800 dark:text-red-200' : 'text-gray-900 dark:text-white'}`}>
                    {title}
                </h3>
                {description ? (
                    <p className="mt-0.5 text-sm text-gray-500 dark:text-white/60">{description}</p>
                ) : null}
            </div>
            <div className="divide-y divide-gray-100 dark:divide-white/5">{children}</div>
        </section>
    );
}

export default function MemberAccountTab({
    member,
    canModify,
    onEditProfile,
    onResetPassword,
    onRemove,
}) {
    const { t } = useTranslation();

    const joinedLabel = member.joined_at
        ? new Date(member.joined_at).toLocaleDateString('it-IT')
        : '-';

    return (
        <div className="w-full space-y-5 pb-4 lg:space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 pb-4 dark:border-white/5 lg:pb-5">
                <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white lg:text-2xl">
                        {t('gym.membersManager.memberDetail.account.title')}
                    </h2>
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-white/60">
                        {t('gym.membersManager.memberDetail.account.subtitle')}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 dark:border-white/10 dark:bg-gray-900/40 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-white/50">
                        {t('gym.membersManager.memberDetail.profile.fields.name')}
                    </p>
                    <p className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white">{member.name}</p>
                </div>
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-white/50">
                        {t('gym.membersManager.memberDetail.profile.fields.username')}
                    </p>
                    <p className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white">{member.username || '-'}</p>
                </div>
                <div className="min-w-0 sm:col-span-2 xl:col-span-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-white/50">
                        {t('gym.membersManager.memberDetail.profile.fields.email')}
                    </p>
                    <p className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white">{member.email}</p>
                </div>
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-white/50">
                        {t('gym.membersManager.memberDetail.profile.fields.joinedAt')}
                    </p>
                    <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{joinedLabel}</p>
                </div>
            </div>

            <div className={`grid grid-cols-1 gap-5 xl:items-start xl:gap-6 ${canModify ? 'xl:grid-cols-2' : ''}`}>
                <AccountPanel
                    title={t('gym.membersManager.memberDetail.account.generalPanel.title')}
                    description={t('gym.membersManager.memberDetail.account.generalPanel.description')}
                >
                    {canModify && (
                        <AccountActionRow
                            icon={UserCog}
                            title={t('gym.membersManager.memberDetail.account.profileSection.title')}
                            description={t('gym.membersManager.memberDetail.account.profileSection.description')}
                        >
                            <button
                                type="button"
                                onClick={onEditProfile}
                                className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:border-indigo-300 hover:bg-indigo-50 dark:border-white/10 dark:bg-gray-900 dark:text-white dark:hover:border-indigo-500/40 dark:hover:bg-indigo-950/30 sm:w-auto sm:min-w-[168px]"
                            >
                                <User size={16} aria-hidden />
                                {t('gym.membersManager.memberDetail.editManagedData')}
                            </button>
                        </AccountActionRow>
                    )}

                    <AccountActionRow
                        icon={BarChart3}
                        title={t('gym.membersManager.memberDetail.account.statsSection.title')}
                        description={t('gym.membersManager.memberDetail.account.statsSection.description')}
                    >
                        <Link
                            to={`/gym/statistics-individual?memberId=${member.id}`}
                            className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/30 dark:bg-indigo-950/40 dark:text-indigo-200 dark:hover:bg-indigo-950/60 sm:w-auto sm:min-w-[168px]"
                        >
                            <MemberViewStatisticsIcon className="h-4 w-4 shrink-0" aria-hidden />
                            {t('gym.membersManager.memberDetail.viewStatistics')}
                        </Link>
                    </AccountActionRow>
                </AccountPanel>

                {canModify && (
                    <div className="flex flex-col gap-5 xl:gap-6">
                        <AccountPanel
                            title={t('gym.membersManager.memberDetail.account.securityPanel.title')}
                            description={t('gym.membersManager.memberDetail.account.securityPanel.description')}
                        >
                            <AccountActionRow
                                icon={KeyRound}
                                title={t('gym.membersManager.memberDetail.account.securitySection.title')}
                                description={t('gym.membersManager.memberDetail.account.securitySection.description')}
                            >
                                <button
                                    type="button"
                                    onClick={() => onResetPassword(member.id, member.name)}
                                    className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 dark:bg-indigo-600 dark:hover:brightness-110 sm:w-auto sm:min-w-[168px]"
                                >
                                    <KeyRound size={16} aria-hidden />
                                    {t('gym.membersManager.memberDetail.account.resetPassword')}
                                </button>
                            </AccountActionRow>
                        </AccountPanel>

                        <AccountPanel
                            variant="danger"
                            title={t('gym.membersManager.memberDetail.account.dangerSection.title')}
                            description={t('gym.membersManager.memberDetail.account.dangerSection.description')}
                        >
                            <AccountActionRow
                                icon={Trash2}
                                variant="danger"
                                title={t('gym.membersManager.deleteClient')}
                                description={t('gym.membersManager.memberDetail.account.dangerSection.hint')}
                            >
                                <button
                                    type="button"
                                    onClick={() => onRemove(member.id, member.name)}
                                    className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 sm:w-auto sm:min-w-[168px]"
                                >
                                    <Trash2 size={16} aria-hidden />
                                    {t('gym.membersManager.deleteClient')}
                                </button>
                            </AccountActionRow>
                        </AccountPanel>
                    </div>
                )}
            </div>
        </div>
    );
}
